import { STORE_RELATIONS_FALLBACK_TEXT } from '../../constants/storeConstants'
import { mergeUniqueStoreRelationSalesmanOptions } from '../../utils/storeRelationDisplayUtils'
import {
  STORE_RELATION_FORM_MODES,
  STORE_RELATION_FORM_REASSIGNMENT_WARNING_MESSAGES,
  STORE_RELATION_FORM_TEXT
} from '../constants/storeRelationFormConstants'
import { buildStoreRelationAssignmentRowOption } from '../utils/storeRelationFormOptionUtils'

export const createStoreRelationFlowState = (
  mode = STORE_RELATION_FORM_MODES.CREATE,
  relationId = null,
  message = ''
) => ({
  mode,
  relationId,
  message
})

export const createStoreRelationReassignModalState = () => ({
  open: false,
  currentRowId: null,
  nextUser: null,
  nextUserSearch: ''
})

export const buildStoreRelationExistingRelationFlowState = (response) => {
  const nextRelationId =
    response?.flow?.store_salesman_relation_id ||
    response?.result?.client_principal?.id ||
    null

  return createStoreRelationFlowState(
    STORE_RELATION_FORM_MODES.EDIT,
    nextRelationId,
    response?.flow?.message || STORE_RELATION_FORM_TEXT.EXISTING_RELATION_MESSAGE
  )
}

export const buildStoreRelationPageTitle = ({
  isResolvedEditMode,
  isPrefilledResolutionMode,
  pageTitleOverride,
  isPrefilledAssignMode,
  clientDbRef,
  relationId,
  routeId
}) => {
  if (isResolvedEditMode) {
    return `${STORE_RELATION_FORM_TEXT.EDIT_TITLE_PREFIX}: ${clientDbRef || relationId || routeId || STORE_RELATIONS_FALLBACK_TEXT}`
  }

  if (isPrefilledResolutionMode) {
    return (
      pageTitleOverride ||
      `${isPrefilledAssignMode
        ? STORE_RELATION_FORM_TEXT.ASSIGN_USER
        : STORE_RELATION_FORM_TEXT.REASSIGN_TITLE_PREFIX}: ${clientDbRef || STORE_RELATIONS_FALLBACK_TEXT}`
    )
  }

  return STORE_RELATION_FORM_TEXT.CREATE_TITLE
}

export const buildStoreRelationPageDescription = ({
  isResolvedEditMode,
  isPrefilledAssignMode,
  isPrefilledResolutionMode
}) => {
  if (isResolvedEditMode) return STORE_RELATION_FORM_TEXT.EDIT_DESCRIPTION
  if (isPrefilledAssignMode) return STORE_RELATION_FORM_TEXT.ASSIGN_DESCRIPTION
  if (isPrefilledResolutionMode) return STORE_RELATION_FORM_TEXT.REASSIGN_DESCRIPTION

  return STORE_RELATION_FORM_TEXT.CREATE_DESCRIPTION
}

export const buildStoreRelationSaveButtonLabel = ({
  isResolvedEditMode,
  isPrefilledAssignMode,
  shouldRenderReadOnlyBaseInfo
}) => {
  if (isResolvedEditMode) return STORE_RELATION_FORM_TEXT.SAVE_CHANGES
  if (isPrefilledAssignMode) return STORE_RELATION_FORM_TEXT.ASSIGN_USER
  if (shouldRenderReadOnlyBaseInfo) return STORE_RELATION_FORM_TEXT.SAVE_CHANGES

  return STORE_RELATION_FORM_TEXT.CREATE_RELATION
}

export const buildStoreRelationRowsRequiringReassignmentLabel = (rows = []) => {
  return rows
    .map((row) => row?.userLabel)
    .filter(Boolean)
    .join(' | ')
}

export const buildStoreRelationFormReassignmentWarningMessages = ({
  hasPendingPrincipalReassignment,
  rowsRequiringReassignmentLabel
}) => {
  return [
    ...(hasPendingPrincipalReassignment
      ? [STORE_RELATION_FORM_REASSIGNMENT_WARNING_MESSAGES.PRINCIPAL]
      : []),
    ...(rowsRequiringReassignmentLabel
      ? [`${STORE_RELATION_FORM_REASSIGNMENT_WARNING_MESSAGES.USERS_PREFIX}${rowsRequiringReassignmentLabel}`]
      : [])
  ]
}

export const buildStoreRelationEffectivePrincipalResolutionContext = ({
  principalResolutionContext,
  principalCandidates = [],
  principalUserOption
}) => {
  if (!principalResolutionContext) return null

  const selectedPrincipalDbRef = principalUserOption?.db_ref || ''
  const selectedPrincipalId = principalUserOption?.id || null
  const candidateRelationIds = principalCandidates
    .map((relation) => relation?.id)
    .filter(Boolean)
  const matchedCandidate = principalCandidates.find((relation) => (
    (
      selectedPrincipalDbRef &&
      relation?.salesman?.db_ref === selectedPrincipalDbRef
    ) || (
      selectedPrincipalId &&
      relation?.salesman?.id === selectedPrincipalId
    )
  )) || null

  return {
    ...principalResolutionContext,
    selected_principal_salesman_id: selectedPrincipalId,
    selected_principal_salesman_db_ref: selectedPrincipalDbRef,
    kept_store_salesman_relation_id: matchedCandidate?.id || null,
    store_salesman_relation_ids_to_remove: matchedCandidate
      ? candidateRelationIds.filter((relationId) => relationId !== matchedCandidate.id)
      : candidateRelationIds,
    create_new_relation: !matchedCandidate
  }
}

export const buildStoreRelationReassignUserOptions = ({
  assignmentRows = [],
  currentRowId = null,
  currentRowDbRef = '',
  searchTerm = '',
  searchedOptions = []
}) => {
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const existingAssignmentOptions = assignmentRows
    .filter((row) => row.id !== currentRowId && !row.requiresReassignment)
    .map((row) => buildStoreRelationAssignmentRowOption(row))
    .filter(Boolean)
    .filter((option) => {
      if (!normalizedSearch) return true

      return option.label.toLowerCase().includes(normalizedSearch)
    })

  return mergeUniqueStoreRelationSalesmanOptions(
    existingAssignmentOptions,
    Array.isArray(searchedOptions) ? searchedOptions : []
  ).filter((option) => (
    option.id !== currentRowId &&
    option.db_ref !== currentRowDbRef
  ))
}

export const buildStoreRelationCurrentReassignUser = (row) => {
  if (!row) return null

  return {
    id: row.id,
    db_ref: row.salesmanDbRef,
    label: row.userLabel
  }
}
